import { CryptoEvent } from '../constants/matrixEvents';
import logger from './logger';

/**
 * A_25514 first-send key-share race guard.
 *
 * OnlySignedDevicesIsolationMode (see matrixService._applyDeviceIsolationPolicy)
 * makes rust-crypto's identityBasedStrategy share megolm keys ONLY to
 * cross-signed (signedByOwner) devices. When a recipient enrols a device and the
 * sender learns of it in the same create→invite→send burst, the sender's LOCAL
 * trust view lags the server by a few hundred ms, so the new (legitimately
 * signed) device is dropped from the FIRST key-share → it stays unable to decrypt
 * until a later send self-heals (too late for the cert's 10s receive poll).
 *
 * This bounded, best-effort pre-send wait lets local trust catch up BEFORE the
 * share so the device is included on the first message. It is OBSERVE-ONLY: it
 * never verifies / cross-signs / mutates trust — it only READS device
 * verification status and waits. It does NOT relax the signed-only filter; on
 * timeout the SDK still makes its normal signed-only decision, so the E2E
 * security posture is unchanged. Tunable; ~2s covers a /keys/query round-trip
 * under cert-mode mTLS latency.
 */
export const RECIPIENT_TRUST_SETTLE_MS = 2000;

// Rooms already gated, so concurrent/subsequent first-sends don't re-wait.
// Bounded (cleared past 1000) so it can't grow without limit.
const trustSettledRooms = new Set();

// Test-only: reset the once-per-room memo between cases.
export const __resetTrustSettledRooms = () => trustSettledRooms.clear();

/**
 * Wait (≤ boundMs) until every device of every recipient reports
 * signedByOwner === true, so the first megolm share includes a
 * freshly-enrolled-but-already-signed device.
 *
 * Recipients are passed EXPLICITLY (not derived from room state): right after
 * createRoom the room's member list / device lists aren't populated yet, so the
 * caller supplies the invited MXIDs it already knows.
 *
 * @param {object} client      Matrix client (getCrypto/getUserId/on/off)
 * @param {string} roomId      used for the once-per-room guard + log correlation
 * @param {string[]} recipientIds  invited user MXIDs (the sender is filtered out)
 * @param {number} boundMs     hard upper bound on the wait
 * @returns {Promise<void>}    never throws; never blocks longer than boundMs
 */
export const awaitRecipientTrustSettled = async (client, roomId, recipientIds, boundMs) => {
  try {
    if (!roomId || trustSettledRooms.has(roomId)) {
      logger.info(`[A25514-GATE] skip room=${roomId} reason=${!roomId ? 'no-roomId' : 'already-gated'}`);
      return;
    }
    if (trustSettledRooms.size > 1000) trustSettledRooms.clear();
    trustSettledRooms.add(roomId); // mark up-front so concurrent sends don't double-wait

    const crypto = client?.getCrypto?.();
    const selfId = client?.getUserId?.();
    const recipients = (Array.isArray(recipientIds) ? recipientIds : [])
      .filter((id) => id && id !== selfId);
    const expectedUsers = recipients.length;

    if (!crypto || expectedUsers === 0) {
      logger.info(`[A25514-GATE] skip room=${roomId} reason=${!crypto ? 'no-crypto' : 'no-recipients'} recipients=${expectedUsers}`);
      return;
    }
    logger.info(`[A25514-GATE] enter room=${roomId} recipients=${recipients.join('|')} boundMs=${boundMs}`);

    // Scan every recipient device. downloadUncached=true FORCES a /keys/query for
    // not-yet-tracked recipients (the freshly-invited case) so we evaluate the
    // REAL device list, not an empty cache. For already-tracked users this is a
    // cached read (no HTTP) and does not re-fetch them.
    const scanRecipients = async (label) => {
      let total = 0;
      let unsigned = 0;
      const perUser = [];
      const info = await crypto.getUserDeviceInfo(recipients, true);
      for (const [userId, devices] of info.entries()) {
        let uTotal = 0;
        let uUnsigned = 0;
        for (const deviceId of devices.keys()) {
          total += 1;
          uTotal += 1;
          const status = await crypto.getDeviceVerificationStatus(userId, deviceId);
          const signed = status ? status.signedByOwner : null;
          if (signed === false) {
            unsigned += 1;
            uUnsigned += 1;
          }
          logger.info(`[A25514-GATE] ${label} dev room=${roomId} user=${userId} device=${deviceId} signedByOwner=${signed}`);
        }
        perUser.push(`${userId}:${uTotal}d/${uUnsigned}u`);
      }
      logger.info(`[A25514-GATE] ${label} scan room=${roomId} users=${info.size}/${expectedUsers} devices=${total} unsigned=${unsigned} [${perUser.join(',')}]`);
      return { total, unsigned, users: info.size };
    };

    // "Settled" requires actually SEEING all recipients' devices AND all signed.
    // total===0 or users<expected means the device list isn't downloaded yet →
    // WAIT, do NOT treat as all-signed (the silent-skip hole that no-op'd diag #4).
    const isSettled = (s) => s.total > 0 && s.unsigned === 0 && s.users >= expectedUsers;

    const t0 = Date.now();
    const first = await scanRecipients('first');
    if (isSettled(first)) {
      logger.info(`[A25514-GATE] settled-fast room=${roomId} devices=${first.total} elapsedMs=${Date.now() - t0}`);
      return;
    }

    // A /keys/query for the changed/new recipient is in flight; DevicesUpdated
    // fires once it lands and the device then verifies as cross-signed. Race that
    // (plus a re-scan) against the bound.
    let outcome = 'timeout';
    await new Promise((resolve) => {
      let settled = false;
      const finish = (why) => {
        if (settled) return;
        settled = true;
        outcome = why;
        clearTimeout(timer);
        client.off?.(CryptoEvent.DevicesUpdated, onDevicesUpdated);
        resolve();
      };
      const recheck = async () => {
        try {
          if (isSettled(await scanRecipients('recheck'))) finish('settled');
        } catch {
          /* best-effort re-check */
        }
      };
      const onDevicesUpdated = (userIds) => {
        if (Array.isArray(userIds) && userIds.some((u) => recipients.includes(u))) recheck();
      };
      const timer = setTimeout(() => finish('timeout'), boundMs);
      client.on?.(CryptoEvent.DevicesUpdated, onDevicesUpdated);
      // listen-then-recheck: cover an update that fired between the first scan and
      // the listener registration.
      recheck();
    });
    const finalScan = await scanRecipients('final');
    logger.info(`[A25514-GATE] outcome room=${roomId} outcome=${outcome} elapsedMs=${Date.now() - t0} finalDevices=${finalScan.total} finalUnsigned=${finalScan.unsigned}`);
  } catch (err) {
    logger.info(`[A25514-GATE] threw room=${roomId} err=${err?.message || err}`);
  }
};
